import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import * as crmApi from "../api/crmApi";
import { crmErrorMessage } from "../api/crmErrors";
import CrmDialog from "../ui/CrmDialog";
import CrmInput from "../ui/CrmInput";
import CrmTextarea from "../ui/CrmTextarea";
import CrmButton from "../ui/CrmButton";
import MergeFieldPicker, { insertAtCaret } from "../ui/MergeFieldPicker";
import { LABEL } from "../ui/classes";
import { CRM } from "../../../constants/testIds/crm";

/**
 * "Send email" composer with merge fields (spec). The body is plain text; the server
 * escapes it, fills the merge fields and adds the marketing footer (C2.07). Only reached
 * for a consented client — the button is disabled otherwise — and the server refuses
 * anyway (409 CLIENT_NOT_CONSENTED).
 */
export default function SendEmailDialog({ client, onClose, onSent }) {
  const t = useT("crm");
  const tc = useT("common");
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [busy, setBusy] = useState(false);
  const area = useRef(null);

  const insert = (token) => {
    const { value, caret } = insertAtCaret(area.current, bodyText, token);
    setBodyText(value);
    requestAnimationFrame(() => { area.current?.focus(); area.current?.setSelectionRange(caret, caret); });
  };

  const send = async () => {
    setBusy(true);
    try {
      await crmApi.sendClientEmail(client.id, { subject: subject.trim(), body_html: bodyText });
      toast.success(t("email.sent"));
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setBusy(false);
    }
  };

  return (
    <CrmDialog open onOpenChange={(o) => { if (!o) onClose(); }} size="lg" testId={CRM.clientEmailDialog}
      title={t("email.title", { name: client.first_name || client.display_name })}
      footer={(
        <>
          <CrmButton variant="link" onClick={onClose}>{t("common.cancel")}</CrmButton>
          <CrmButton onClick={send} disabled={busy || !subject.trim() || !bodyText.trim()} data-testid={CRM.clientEmailSend}>{t("common.send")}</CrmButton>
        </>
      )}>
      <div className="space-y-3">
        <div>
          <label htmlFor="crm-email-subject" className={LABEL}>{t("email.subject")}</label>
          <CrmInput id="crm-email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid={CRM.clientEmailSubject} />
        </div>
        <div>
          <label htmlFor="crm-email-body" className={LABEL}>{t("email.body")}</label>
          <CrmTextarea id="crm-email-body" ref={area} rows={8} value={bodyText} onChange={(e) => setBodyText(e.target.value)} data-testid={CRM.clientEmailBody} />
        </div>
        <MergeFieldPicker onInsert={insert} />
      </div>
    </CrmDialog>
  );
}
